import { useState, useEffect, lazy, Suspense } from 'react';
import { HashRouter, Route, Routes } from 'react-router-dom';
import {
  axios,
  constants,
  Notification,
  AppLayout,
  PrivateRoute,
  PublicRoute,
  UpdatePassword,
  Loader,
  ErrorBoundary,
  NotFoundPage,
  SignedOutDialog,
  useAuth,
} from '@bento-dental/core';
import type { AppNavigationLink, AppNavigationSubLink } from '@bento-dental/core';
import { StorageProvider } from '@bento-dental/core/hooks';
import SignIn from './components/Account/SignIn';
import HeaderView from './components/Header/HeaderView';
const ManageOrtho = lazy(() => import('./components/Ortho/ManageOrtho'));
const ManagePlan = lazy(() => import('./components/Ortho/ManagePlan'));
const StartPaymentPlan = lazy(() => import('./components/Ortho/StartPaymentPlan'));
const OrthoPaymentPlans = lazy(() => import('./components/Ortho/OrthoPaymentPlans'));
const CreateReimbursement = lazy(() => import('./components/Reimbursement/CreateReimbursement'));
const Reimbursement = lazy(() => import('./components/Reimbursement/Reimbursement'));
const ReimbursementDetails = lazy(() => import('./components/Reimbursement/ReimbursementDetails'));
const EditInOfficePlan = lazy(() => import('./components/InOfficePlans/EditInOfficePlan'));
const MemberDetails = lazy(() => import('./components/Members/MemberDetails'));
const ManageInOfficePlan = lazy(() => import('./components/InOfficePlans/ManageInOfficePlan'));
const PatientEligibility = lazy(() => import('./components/Members/PatientEligibility'));
const AdminUsers = lazy(() => import('./components/Users/AdminUsers'));
const AllUsers = lazy(() => import('./components/Users/AllUsers'));
const Claims = lazy(() => import('./components/Claims/Claims'));
const Members = lazy(() => import('./components/Members/Members'));
const Practices = lazy(() => import('./components/Practices/Practices'));
const CandidatePractices = lazy(() => import('./components/CandidatePractices/CandidatePractices'));
const Employers = lazy(() => import('./components/Employers/Employers'));
const EmployerDashboard = lazy(() => import('./components/Employers/Dashboard/EmployerDashboard'));
const InOfficePlans = lazy(() => import('./components/InOfficePlans/InOfficePlans'));
const SignUp = lazy(() => import('./components/Account/SignUp'));
const Debug = lazy(() => import('./components/Account/Debug'));
const CompleteEmailChange = lazy(() => import('./components/Account/CompleteEmailChange'));
const PracticeDetailView = lazy(() => import('./components/Practices/PracticeDetailView'));
const EmployerDetails = lazy(() => import('./components/Employers/EmployerDetails'));
const ClaimsEditView = lazy(() => import('./components/Claims/ClaimsEditView'));
const CustomizedPlans = lazy(() => import('./components/CustomizedPlans/CustomizedPlans'));
const CustomizedPlanTabView = lazy(
  () => import('./components/CustomizedPlans/CustomizedPlanTabView')
);
const ClaimInvoices = lazy(() => import('./components/ClaimInvoices/ClaimInvoices'));
const Workflows = lazy(() => import('./components/Claims/Workflows'));
const EDIImport = lazy(() => import('./components/EDIImport/EDIImport'));
const DashboardAdmin = lazy(() => import('./components/Dashboard/DashboardAdmin'));
const PricingTool = lazy(() => import('./components/PricingTool/PricingTool'));
const AccessPoints = lazy(() => import('./components/AccessPoints/AccessPoints'));
const ClinicalReviews = lazy(() => import('./components/ClinicalReviews/ClinicalReviews'));
const ClinicalReviewDetail = lazy(
  () => import('./components/ClinicalReviews/ClinicalReviewDetail')
);
const FeeSchedules = lazy(() => import('./components/FeeSchedules/FeeSchedules'));

const canAccessPricingTool = (role?: string) =>
  ['admin_admin', 'admin_cs', 'admin_csm', 'admin_csm_admin'].includes(role ?? '');

const canAccessFeeSchedules = (role?: string) => ['admin_admin', 'admin_csm'].includes(role ?? '');

const getPlanLinks = (role?: string): AppNavigationSubLink[] => {
  let links: AppNavigationSubLink[] = [];
  switch (role) {
    case 'admin_admin':
      links = [
        { title: 'In-Office plans', id: 'inop-plans', route: '/plans' },
        { title: 'Customized plans', route: '/customized-plans', id: 'customizedplan' },
        { title: 'Dashboard', route: '/dashboard', id: 'dashboard' },
      ];
      break;
    case 'admin_cs':
    case 'admin_csm':
    case 'admin_csm_admin':
      links = [
        { title: 'In-Office plans', id: 'inop-plans', route: '/plans' },
        { title: 'Dashboard', route: '/dashboard', id: 'dashboard' },
      ];
      break;
    default:
      links = [];
  }
  return links;
};

const getNavLinks = (role?: string): AppNavigationLink[] => {
  if (role === 'admin_clinical_reviewer') {
    return [{ title: 'Clinical reviews', id: 'clinical-reviews', route: '/clinical-reviews' }];
  }

  const links: AppNavigationLink[] = [
    { title: 'Search for members', id: 'members', route: ['/members', '/'] },
    { title: 'Practices', id: 'practices', route: '/practices' },
    { title: 'Candidate practices', id: 'candidates', route: '/candidates' },
    {
      title: 'Employers',
      id: 'employers',
      route: '/employers',
      items: [{ title: 'Dashboard', id: 'employers-dashboard', route: '/employers/dashboard' }],
    },
    { title: 'Reimbursements', id: 'reimbursements', route: '/reimbursements' },
    {
      title: 'Ortho Plans',
      id: 'ortho-payment-plans',
      route: '/ortho-payment-plans',
    },
    {
      title: 'Plans',
      id: 'plans',
      route: '/plans',
      items: getPlanLinks(role),
    },
  ];

  if (canAccessPricingTool(role)) {
    links.push({ title: 'Pricing Tool', id: 'pricing-tool', route: '/pricing-tool' });
  }

  if (canAccessFeeSchedules(role)) {
    links.push({ title: 'Fee Schedules', id: 'fee-schedules', route: '/fee-schedules' });
  }

  links.push(
    { title: 'Access points', id: 'access-points', route: '/access-points' },
    { title: 'Admin users', id: 'admin-users', route: '/admin-users' },
    { title: 'Portal users', id: 'all-users', route: '/all-users' },
    {
      title: 'Claims',
      id: 'claims',
      route: '/claims',
      items: [{ title: 'Workflows', id: 'claim-workflows', route: '/claim-workflows' }],
    },
    {
      title: 'Claims invoices',
      id: 'claims-invoices',
      route: '/invoices',
    },
    { title: 'Clinical reviews', id: 'clinical-reviews', route: '/clinical-reviews' },
    { title: 'EDI Import', id: 'edi-import', route: '/edi-import' }
  );

  return links;
};

type CurrentUser = {
  role?: string;
  username?: string;
  user_id?: number;
};

function App() {
  const { isLoggedIn, shouldRender, initializer } = useAuth();
  const [debugOpen, setDebugOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<CurrentUser>({});

  function checkUserSignUp() {
    return axios.get('/mast-alto/check-user-signup/').then((response) => {
      setCurrentUser(response.data.data);
    });
  }

  useEffect(() => {
    if (isLoggedIn) {
      checkUserSignUp();
    }
  }, [isLoggedIn]);

  const { role, username, user_id: userId } = currentUser;

  const isAdminSales = role === 'admin_sales';
  const isAdminClinicalReviewer = role === 'admin_clinical_reviewer';

  const value = {
    username,
    role,
    isAdminSales,
    isAdminClinicalReviewer,
    enviroment: initializer?.environment,
    reinitialize: initializer?.reinitialize,
  };

  const envProps = {
    debug: initializer?.debug,
    version: initializer?.version,
    environment: initializer?.environment,
    serverDateTime: initializer?.serverDateTime,
  };

  return (
    <StorageProvider value={value}>
      <Suspense fallback={<Loader />}>
        <ErrorBoundary>
          <HashRouter>
            {shouldRender && (
              <AppLayout
                links={getNavLinks(role)}
                hideLayout={!isLoggedIn || isAdminSales}
                envProps={{
                  ...envProps,
                  onClick() {
                    setDebugOpen(true);
                  },
                }}
                header={<HeaderView />}
              >
                <Suspense fallback={<Loader />}>
                  <Routes>
                    {/* private routes */}
                    <Route element={<PrivateRoute />}>
                      <Route path="/pricing-tool" element={<PricingTool />} />
                      <Route path="/fee-schedules" element={<FeeSchedules />} />
                      <Route path="/members/:memberId" element={<MemberDetails />} />
                      <Route path="/members" element={<Members />} />
                      <Route path="/admin-users" element={<AdminUsers />} />
                      <Route path="/all-users" element={<AllUsers />} />
                      <Route path="/claims-edit/:claimId" element={<ClaimsEditView />} />
                      <Route path="/claims" element={<Claims />} />
                      <Route path="/claim-workflows" element={<Workflows />} />
                      <Route path="/practices/:practiceId" element={<PracticeDetailView />} />
                      <Route path="/practices" element={<Practices />} />
                      <Route path="/candidates" element={<CandidatePractices />} />
                      <Route path="/employers/dashboard" element={<EmployerDashboard />} />
                      <Route path="/employers/:employerId" element={<EmployerDetails />} />
                      <Route path="/employers" element={<Employers />} />
                      <Route path="/reimbursements" element={<Reimbursement />} />
                      <Route path="/ortho-payment-plans" element={<OrthoPaymentPlans />} />
                      <Route
                        path="/reimbursements/:reimbursementId"
                        element={<ReimbursementDetails />}
                      />
                      <Route
                        path="/reimbursements/:reimbursementId/other-claims"
                        element={<ReimbursementDetails />}
                      />
                      <Route path="/plans" element={<InOfficePlans />} />
                      <Route path="/manage-ortho-plan/:planId" element={<ManagePlan />} />
                      <Route
                        path="/manage-ortho/:memberId/start-plan"
                        element={<StartPaymentPlan />}
                      />
                      <Route path="/manage-ortho/:memberId" element={<ManageOrtho />} />
                      <Route
                        path="/create-reimbursement/:memberId"
                        element={<CreateReimbursement />}
                      />
                      <Route
                        path="/patient-eligibility/:memberId"
                        element={<PatientEligibility />}
                      />
                      <Route
                        path="/in-office-plans/:inOfficePlanId/edit"
                        element={<EditInOfficePlan />}
                      />
                      <Route
                        path="/manage-in-office-plan/:inOfficePlanId/:tab?"
                        element={<ManageInOfficePlan />}
                      />
                      <Route path="/customized-plans" element={<CustomizedPlans />} />
                      <Route path="/CustomizedPlanView/:name" element={<CustomizedPlanTabView />} />
                      <Route path="/invoices" element={<ClaimInvoices />} />
                      <Route path="/edi-import" element={<EDIImport />} />
                      <Route path="/" element={<Members />} />
                      <Route path="/dashboard" element={<DashboardAdmin />} />
                      <Route path="/access-points" element={<AccessPoints />} />
                      <Route path="/clinical-reviews" element={<ClinicalReviews />} />
                      <Route
                        path="/clinical-reviews/:reviewId"
                        element={<ClinicalReviewDetail />}
                      />
                    </Route>
                    {/* public routes */}
                    <Route element={<PublicRoute />}>
                      <Route
                        path="/update-password"
                        element={<UpdatePassword portal={constants.PORTALS.ADMIN} />}
                      />
                      <Route path="/sign-up" element={<SignUp />} />
                      <Route path="/signup" element={<SignUp />} />
                      <Route path="/sign-in" element={<SignIn />} />
                      <Route path="/signin" element={<SignIn />} />
                    </Route>

                    <Route path="/complete-email-change" element={<CompleteEmailChange />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Suspense>
                <Debug
                  isOpen={debugOpen}
                  handleClose={() => {
                    setDebugOpen(false);
                  }}
                />
              </AppLayout>
            )}
          </HashRouter>
        </ErrorBoundary>
      </Suspense>
      <SignedOutDialog portal={constants.PORTALS.ADMIN} />
      <Notification />
    </StorageProvider>
  );
}

export default App;
